// src/components/Footer.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';

const SOCIAL_LINKS = [
  { icon: 'fab fa-facebook-f', url: 'https://www.facebook.com/groups/355271187923008', label: 'Facebook' },
  { icon: 'fab fa-linkedin-in', url: 'https://linkedin.com/company/iiee-ercsa', label: 'LinkedIn' },
  { icon: 'fab fa-x-twitter',   url: 'https://twitter.com/iiee_ercsa',           label: 'X (Twitter)' },
  { icon: 'fab fa-instagram',   url: 'https://instagram.com/iiee.ercsa',         label: 'Instagram' },
  { icon: 'fab fa-youtube',     url: 'https://youtube.com/c/iieeercsa',          label: 'YouTube' },
];

/* ================================================================== */
/* Back to Top button                                                  */
/* ================================================================== */

const BackToTop = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          initial={{ opacity: 0, scale: 0.6, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.6, y: 10 }}
          transition={{ duration: 0.2 }}
          onClick={scrollToTop}
          aria-label="Back to top"
          title="Back to top"
          className="fixed right-6 z-40 w-11 h-11 rounded-full bg-orange hover:bg-orange-dark text-white shadow-lg hover:shadow-xl flex items-center justify-center transition-colors group bottom-[calc(6rem+env(safe-area-inset-bottom))] lg:bottom-6"
        >
          <i className="fas fa-arrow-up text-base group-hover:-translate-y-0.5 transition-transform" aria-hidden="true" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

/* ================================================================== */
/* Install Instructions Modal                                          */
/* ================================================================== */

const InstallInstructionsModal = ({ open, onClose, release, onDownload }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const steps = [
    {
      icon: 'fa-download',
      title: 'Download the APK',
      body: 'Tap the button below. The file will download to your phone.',
    },
    {
      icon: 'fa-shield-halved',
      title: 'Allow "Install unknown apps"',
      body: 'Android will warn you because the app isn\'t from the Play Store. Tap Settings → enable "Allow from this source" for your browser (Chrome, Firefox, etc.).',
    },
    {
      icon: 'fa-triangle-exclamation',
      title: 'Confirm the install',
      body: 'Play Protect may show a warning. Tap "Install anyway" or "More details → Install anyway". This is normal for sideloaded APKs.',
    },
    {
      icon: 'fa-mobile-screen-button',
      title: 'Open IIEE-ERCSA',
      body: 'Once installed, open the app from your home screen or app drawer. Sign in with the same account you use on the website.',
    },
  ];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-labelledby="install-modal-title"
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            className="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-5 py-4 flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                <i className="fab fa-android text-green-600 text-xl" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 id="install-modal-title" className="text-base font-bold text-gray-900 dark:text-white">
                  Install IIEE-ERCSA for Android
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {release?.version
                    ? `Version ${release.version} · `
                    : ''}
                  ~4 steps · 1 minute
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {steps.map((s, i) => (
                <div key={s.title} className="flex gap-3">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-orange/10 text-orange flex items-center justify-center font-bold text-xs">
                      {i + 1}
                    </div>
                    {i < steps.length - 1 && (
                      <div className="w-px flex-1 bg-gray-200 dark:bg-gray-700 mt-1" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 pb-2">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <i className={`fas ${s.icon} text-orange text-xs`} aria-hidden="true" />
                      {s.title}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mx-5 mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex gap-2 text-xs text-yellow-800 dark:text-yellow-200">
              <i className="fas fa-circle-info mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span>
                This app is distributed directly by IIEE-ERCSA, not through Google Play.
                The "unknown apps" warning is expected and safe.
              </span>
            </div>

            <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t dark:border-gray-700 px-5 py-3 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  onDownload();
                  onClose();
                }}
                className="flex-1 bg-orange hover:bg-orange-dark text-white py-2.5 rounded-lg text-sm font-semibold transition active:scale-[0.99]"
              >
                <i className="fas fa-download mr-1.5" aria-hidden="true" />
                Continue to download
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 py-2.5 rounded-lg text-sm font-medium transition"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ================================================================== */
/* Footer                                                              */
/* ================================================================== */

const Footer = () => {
  const { user, isAdmin, isBoard } = useAuth();
  const currentYear = new Date().getFullYear();
  const canManageApp = isAdmin || isBoard;

  const [release, setRelease] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'appReleases', 'android'),
      (snap) => setRelease(snap.exists() ? snap.data() : null),
      (err) => console.warn('appReleases listener:', err),
    );
    return () => unsub();
  }, []);

  const exploreLinks = [
    { name: 'Home',   path: '/' },
    { name: 'Board',  path: '/board' },
    { name: 'About',  path: '/about' },
    ...(user ? [{ name: 'Members', path: '/members' }] : []),
  ];

  const accountLinks = user
    ? [
        { name: 'Profile',       path: '/profile' },
        { name: 'Notifications', path: '/notifications' },
        { name: 'Settings',      path: '/settings' },
      ]
    : [
        { name: 'Sign In',   path: '/login' },
        { name: 'Register',  path: '/register' },
      ];

  const handleDownloadClick = (e) => {
    if (!release?.url) return;
    e.preventDefault();
    setShowInstallModal(true);
  };

  const performDownload = () => {
    if (!release?.url) return;
    window.open(release.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <footer className="bg-gray-900 dark:bg-gray-950 text-gray-400 border-t border-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-6">
            <div className="col-span-2 md:col-span-1">
              <Link to="/" className="flex items-center gap-2 mb-2">
                <img src="/images/iiee-logo.png" alt="IIEE-ERCSA" className="h-7 w-7 object-contain" />
                <span className="text-sm font-bold text-orange">IIEE-ERCSA</span>
              </Link>
              <p className="text-[11px] leading-relaxed text-gray-500">
                Institute of Integrated Electrical Engineers —
                Eastern Region Chapter, Saudi Arabia.
              </p>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-white mb-2">Explore</h4>
              <ul className="space-y-1.5 text-[11px]">
                {exploreLinks.map((l) => (
                  <li key={l.path}>
                    <Link to={l.path} className="hover:text-orange transition-colors">{l.name}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-white mb-2">
                {user ? 'Account' : 'Get Started'}
              </h4>
              <ul className="space-y-1.5 text-[11px]">
                {accountLinks.map((l) => (
                  <li key={l.path}>
                    <Link to={l.path} className="hover:text-orange transition-colors">{l.name}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-white mb-2">Mobile App</h4>
              <ul className="space-y-1.5 text-[11px]">
                {release?.url ? (
                  <li>
                    <a
                      href={release.url}
                      onClick={handleDownloadClick}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 hover:text-orange transition-colors"
                      title={`Download Android app${release.version ? ` v${release.version}` : ''} — install instructions will show`}
                    >
                      <i className="fab fa-android text-green-500 text-sm" aria-hidden="true" />
                      <span>Download APK{release.version ? ` (v${release.version})` : ''}</span>
                    </a>
                  </li>
                ) : (
                  <li className="text-gray-500">Android app coming soon</li>
                )}
                {canManageApp && (
                  <li>
                    <Link
                      to="/admin/android-app"
                      className="inline-flex items-center gap-1.5 text-orange hover:text-orange-light transition-colors font-medium"
                      title="Upload new Android build (admin only)"
                    >
                      <i className="fas fa-cloud-upload-alt text-[10px]" aria-hidden="true" />
                      <span>{release?.url ? 'Upload new build' : 'Upload first build'}</span>
                      <span className="text-[9px] px-1 rounded bg-orange/20 text-orange">admin</span>
                    </Link>
                  </li>
                )}
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-white mb-2">Contact</h4>
              <ul className="space-y-1.5 text-[11px]">
                <li>Al Khobar, KSA</li>
                <li>
                  <a href="mailto:info@iiee-ercsa.org" className="hover:text-orange transition-colors">
                    info@iiee-ercsa.org
                  </a>
                </li>
                <li>
                  <a href="tel:+966541652438" className="hover:text-orange transition-colors">
                    +966 54 165 2438
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="flex items-center gap-3 mb-5">
            {SOCIAL_LINKS.map((s) => (
              <a
                key={s.label}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                title={s.label}
                className="w-7 h-7 rounded-full bg-gray-800 hover:bg-orange text-gray-400 hover:text-white flex items-center justify-center transition-colors"
              >
                <i className={`${s.icon} text-[10px]`} aria-hidden="true" />
              </a>
            ))}
          </div>

          <div className="border-t border-gray-800 pt-4 flex flex-col sm:flex-row justify-between items-center gap-2 text-[10px] text-gray-500">
            <p>© 1996–{currentYear} IIEE-ERCSA</p>
            <div className="flex gap-4">
              <Link to="/board" className="hover:text-orange transition-colors">Board</Link>
              <Link to="/about" className="hover:text-orange transition-colors">About</Link>
              <a href="mailto:info@iiee-ercsa.org" className="hover:text-orange transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>

      {release?.url && (
        <InstallInstructionsModal
          open={showInstallModal}
          onClose={() => setShowInstallModal(false)}
          release={release}
          onDownload={performDownload}
        />
      )}

      <BackToTop />
    </>
  );
};

export default Footer;